import { useTranslation } from "react-i18next";
import { CustomTable } from "shared/ui/customTable/CustomTable";
import HandbookFormModal from "features/handbook/ui/form/HandbookFormModal";
import styles from "./AllServicesPage.module.css";
import { Button, Input, message, Modal } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useAddServiceMutation,
  useEditServiceMutation,
  useLazyGetServiceListQuery,
  useRemoveServiceMutation,
} from "features/service/api/serviceListApi";
import { useSelector } from "react-redux";
import { selectApiVersion } from "shared/store/configSlice";
import { ServiceList } from "features/service/model/serviceList";
import { useSearchPagination } from "shared/hooks/useSearchPagination";

const DEFAULT_PAGE_SIZE = 10;

export default function AllServicesPage() {
  const { t } = useTranslation();
  const apiVersion = useSelector(selectApiVersion);

  const {
    pagination,
    search,
    debouncedSearch,
    handleChangeSearch,
    handlePageChange,
  } = useSearchPagination({ defaultPageSize: DEFAULT_PAGE_SIZE });

  const [fetchList, { data, isFetching }] = useLazyGetServiceListQuery();
  const [add, { isLoading: isAdding }] = useAddServiceMutation();
  const [edit, { isLoading: isEditing }] = useEditServiceMutation();
  const [remove] = useRemoveServiceMutation();

  useEffect(() => {
    fetchList({
      page: pagination.current ?? 1,
      size: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
    });
  }, [fetchList, apiVersion, pagination.current, pagination.pageSize]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows: ServiceList[] = data?.res?.content ?? [];
  const total = data?.res?.totalElements ?? 0;
  const currentPage = data?.res?.page ?? pagination.current ?? 1;
  const currentSize =
    data?.res?.size ?? pagination.pageSize ?? DEFAULT_PAGE_SIZE;

  const filteredRowsOnPage = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const ru = (r.nameRu ?? "").toLowerCase();
      const ky = (r.nameKy ?? "").toLowerCase();
      return ru.includes(q) || ky.includes(q);
    });
  }, [rows, debouncedSearch]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ServiceList | null>(null);

  const openCreate = useCallback(() => {
    setEditingRow(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: ServiceList) => {
    setEditingRow(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleSubmit = useCallback(
    async (payload: any) => {
      try {
        if (editingRow?.code) {
          await edit({ body: { ...editingRow, ...payload } }).unwrap();
          message.success("Запись обновлена");
        } else {
          await add({ body: payload }).unwrap();
          message.success("Запись создана");
        }
        setModalOpen(false);
        await fetchList({
          page: pagination.current ?? 1,
          size: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
        });
      } catch (e) {
        console.error(e);
        message.error("Не удалось сохранить");
      }
    },
    [add, edit, editingRow, fetchList, pagination.current, pagination.pageSize]
  );

  const handleDelete = useCallback(
    (row: ServiceList) => {
      Modal.confirm({
        title: t("handbook.modal.delete_title", "Удалить запись?"),
        content: row.nameRu || row.nameKy || `#${row.code}`,
        okText: t("common.delete", "Удалить"),
        cancelText: t("common.cancel", "Отмена"),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await remove({ code: row.code }).unwrap();
            await fetchList({
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
            });

            message.success("Запись удалена");
          } catch (e) {
            console.error(e);
            message.error("Не удалось удалить");
          }
        },
      });
    },
    [t, remove, fetchList, pagination.current, pagination.pageSize]
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.title}>
          {t("handbook.table.allServices")} - {total}
        </div>
        <div className={styles.search}>
          <div className={styles.searchCard}>
            <Input
              placeholder={t("handbook.table.search", "Поиск")}
              value={search}
              size="large"
              allowClear
              onChange={(e) => handleChangeSearch(e.target.value)}
            />
          </div>
          <Button
            type="primary"
            size="large"
            onClick={openCreate}
            style={{ marginLeft: 10 }}
          >
            {t("handbook.table.create", "Создать")}
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <CustomTable
          data={filteredRowsOnPage}
          isFetching={isFetching}
          pagination={{ current: currentPage, pageSize: currentSize, total }}
          onPageChange={handlePageChange}
          onEdit={openEdit}
          onDelete={handleDelete}
          showService={false}
        />
      </div>

      <HandbookFormModal
        isAllServices
        isAllHandbooks={false}
        open={modalOpen}
        loading={isAdding || isEditing}
        initial={editingRow}
        onCancel={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
